import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploader({ currentImage, onImageChange }) {
  const { admin } = useAdminAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || "");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);

    try {
      // Get presigned URL from backend
      const presignRes = await fetch(`${API_URL}/api/admin/upload/presign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
        }),
      });

      if (!presignRes.ok) throw new Error("Failed to get upload URL");
      const { presignedUrl, publicUrl } = await presignRes.json();

      // Upload directly to S3
      const uploadRes = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadRes.ok) throw new Error("Upload to storage failed");

      setPreview(publicUrl);
      onImageChange(publicUrl);
    } catch (err) {
      console.error("Upload failed:", err);
      setPreview(currentImage || "");
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleRemove = () => {
    setPreview("");
    onImageChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div data-testid="image-uploader">
      {preview ? (
        <div className="relative rounded-lg overflow-hidden border border-[#2a2a2a] bg-[#1a1a1a]">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-40 object-cover"
            data-testid="image-preview"
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center hover:bg-black"
            data-testid="remove-image-button"
          >
            <X size={14} className="text-white" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full h-32 border-2 border-dashed border-[#2a2a2a] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#D8C08F]/50 transition-colors"
          data-testid="upload-trigger"
        >
          <Upload size={20} className="text-[#555]" />
          <span className="text-[#555] text-xs font-sans">
            Click to upload image
          </span>
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleUpload}
        className="hidden"
        data-testid="file-input"
      />
    </div>
  );
}
